import { LoadingButton } from "@mui/lab";
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { t } from "i18next";
import { toNumber } from "lodash";
import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useHorse } from "../../hooks/useHorse";
import { THorse } from "../../shared/types/horse.type";
import { Ttraining } from "../../shared/types/training.type";
import { TUser } from "../../shared/types/user.type";
import { RootState } from "../../store";
import {
  setEditHorseHrModal,
  setHorseHrExams,
  setHorseToEdit,
} from "../../store/reducers/modal.reducer";
import HrHorseExams from "../horse/HrHorseExams";
import BaseModal from "./BaseModal";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

function EditHorseHrModal() {
  const open = useSelector<RootState>(
    (state) => state.modals.horseHrModal,
  ) as boolean;
  const previous_horse_exams = useSelector<RootState>(
    (state) => state.modals.horseHrExams,
  ) as Ttraining[];
  const horse = useSelector<RootState>(
    (state) => state.modals.horseToEdit,
  ) as THorse | null;
  const user = useSelector<RootState>((state) => state.user.user) as TUser;
  const dispatch = useDispatch();
  const { updateHorseHrMutation } = useHorse({ enableQuery: false });
  const query = useQueryClient();
  //form
  const [max_heart_rate, setMaxHr] = useState<number | undefined>(undefined);
  const [isRestro, setIsRestro] = useState(false);
  const [date, setDate] = useState<dayjs.Dayjs | null>(
    dayjs().utc(true).startOf("day"),
  );

  const [selected, setSelected] = useState<Ttraining | undefined>(undefined);

  useEffect(() => {
    if (selected) {
      let value = selected?.insight?.max_heart_rate
        ? Number(selected?.insight?.max_heart_rate)
        : 0;

      setMaxHr(value);
    }
  }, [selected?.id]);

  useEffect(() => {
    setMaxHr(horse?.max_heart_rate_data?.max_heart_rate);
  }, [horse?.max_heart_rate_data?.max_heart_rate]);

  const handleClose = () => {
    dispatch(setHorseToEdit(null));
    dispatch(setEditHorseHrModal(false));
    dispatch(setHorseHrExams([]));
    setSelected(undefined);
  };

  const clearInputs = () => {
    setMaxHr(0);
    setIsRestro(false);
    setDate(null);
  };

  const updateHorse = async () => {
    if (!horse || !user) return;
    try {
      await updateHorseHrMutation.mutateAsync({
        trainer: user["@id"],
        horse: horse["@id"],
        max_heart_rate: String(max_heart_rate),
        ...(isRestro && date
          ? { start_date: date.utc(true).toISOString() }
          : { start_date: dayjs().utc(true).toISOString() }),
      });
      handleClose();
      clearInputs();
      toast(t("addHorse:editSuccess"), { type: "success" });
      query.refetchQueries({ queryKey: ["horse", horse.id] });
    } catch (error) {
      toast(t("error:generic"), { type: "error" });
    }
  };

  if (!open) return null;

  return (
    <BaseModal
      open={open}
      handleClose={handleClose}
      title="parameters:maxHeartRate"
      width={500}
    >
      <Stack direction={"column"} spacing={2}>
        <HrHorseExams
          selected={selected}
          onSelect={(exam) => setSelected(exam)}
          exams={previous_horse_exams}
        />
        <Divider />
        <FormControl>
          <InputLabel> {t("units:bpm")}</InputLabel>
          <OutlinedInput
            endAdornment={
              <InputAdornment position="end">{t("units:bpm")}</InputAdornment>
            }
            label={t("units:bpm")}
            value={max_heart_rate}
            onChange={(e) =>
              setMaxHr(e.target.value ? toNumber(e.target.value) : undefined)
            }
            type="number"
          ></OutlinedInput>
        </FormControl>
        <FormControlLabel
          checked={isRestro}
          onChange={(_, checked) => {
            setIsRestro(checked);
            if (!checked) {
              setDate(null);
            }
          }}
          control={<Checkbox sx={{ pl: 0 }} />}
          label={t("parameters.applyRetrospectively")}
        />
        {isRestro && (
          <DatePicker
            onChange={(value) => {
              if (value) {
                setDate(dayjs(value).utc(true).startOf("day"));
              } else {
                setDate(null);
              }
            }}
            value={date}
            maxDate={dayjs()} //maxDate
            label={t("date")}
          />
        )}
        <Box display={"flex"} justifyContent={"end"}>
          <LoadingButton
            disabled={!max_heart_rate}
            loading={updateHorseHrMutation.isLoading}
            onClick={updateHorse}
            variant="contained"
          >
            {t("button:save")}
          </LoadingButton>
        </Box>
      </Stack>
    </BaseModal>
  );
}

export default EditHorseHrModal;
